<template>
  <div>
    <b-modal
      id="test-version"
      ok-only
      ok-title="Done"
      centered
      size="xl"
      title="Test Pricing"
      ok-variant="success"
      no-close-on-backdrop
      @ok="onDone"
    >
      <b-form>
        <b-row>
          <b-col 
            v-if="currentZones.length > 1"
            md="4"
          >
            <b-form-group
              label="Test Zone"
            >
              <v-select
                v-model="selectedZone"
                :options="zoneOptions"
                label="name"
                :reduce="zone => zone.zone_id"
                @input="onZoneChange"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group
              label="Pickup"
            >
              <v-select
                v-model="pickupPlace"
                placeholder="Start typing to search"
                label="description"
                :options="pickupPlaces"
                :reduce="opt => opt.place_id"
                @search="fetchpickupPlaces"
                @input="onSelectedPlace('pickup')"
              />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group
              label="Dropoff"
            >
              <v-select
                v-model="dropoffPlace"
                placeholder="Start typing to search"
                label="description"
                :options="dropoffPlaces"
                :reduce="opt => opt.place_id"
                @search="fetchDropoffPlaces"
                @input="onSelectedPlace('dropoff')"
              />
            </b-form-group>
          </b-col>
          <b-col md="2" />
          <b-col
            md="2"
            class="d-flex align-items-center"
          >
            <b-button
              variant="primary"
              :disabled="calculatingEstimate || (!dropoffPlace || !pickupPlace)"
              block
              @click="calcEstimate"
            >
              Calculate Estimate
            </b-button>
          </b-col>
          <b-col
            v-if="estimateCalculated"
            md="12"
            style="display:flex;justify-content:center"
            class="mt-1 mb-3"
          >
            <div
              class="d-flex justify-content-center"
              style="gap:50px;border:1px solid gray;padding:15px 30px;border-radius:10px;width:900px"
            >
              <div>
                <strong>Est Distance: </strong>
                <span>{{ matrixDistance.text }}</span>
              </div>
              <div>
                <strong>Est Time: </strong>
                <span>{{ matrixTime.text }}</span>
              </div>
              <div>
                <strong>Est Fare: </strong>
                <span>{{ totalFare }}</span>
              </div>
              <div>
                <strong>Peak Factor: </strong>
                <span>{{ peakFactor }}</span>
              </div>
            </div>
          </b-col>
          <b-col
            v-if="isUpfrontEstimatedFieldsShown"
            md="4"
          >
            <b-form-group
              label="Actual Distance (KM)"
              label-for="actual-distance"
            >
              <b-form-input
                id="actual-distance"
                v-model.number="version.total_distance"
                type="number"
                step=".01"
                required
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="isUpfrontEstimatedFieldsShown"
            md="4"
          >
            <b-form-group
              label="Actual Time (Minutes)"
              label-for="actual-time"
            >
              <b-form-input
                id="actual-time"
                v-model.number="version.total_time"
                type="number"
                step=".01"
                required
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="isUpfrontEstimatedFieldsShown"
            md="2"
            class="d-flex align-items-center"
          >
            <b-form-group
              label="Total Arrival Wait Time (Minutes)"
              label-for="test-total-wait-time"
            >
              <b-form-input
                id="test-total-wait-time"
                v-model.number="version.total_arrival_wait_time"
                type="number"
                required
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="isUpfrontEstimatedFieldsShown"
            md="2"
            class="d-flex align-items-center"
          >
            <b-button
              variant="primary"
              :disabled="!estimateCalculated || calculatingBill || (!version.total_time || !version.total_distance)"
              block
              @click="calcBill"
            >
              Calculate Bill
            </b-button>
          </b-col>
          <b-col
            v-if="billCalculated"
            md="12"
            style="display:flex;justify-content:center"
            class="mt-1 mb-3"
          >
            <div
              class="d-flex justify-content-center"
              style="gap:50px;border:1px solid gray;padding:15px 30px;border-radius:10px;width:900px"
            >
              <div>
                <strong>Chosen Distance: </strong>
                <span>{{ billChosenDistance }}</span>
              </div>
              <div>
                <strong>Chosen Time: </strong>
                <span>{{ billChosenTime }}</span>
              </div>
              <div>
                <strong>Total Bill: </strong>
                <span>{{ billTotal }}</span>
              </div>
              <div>
                <strong>Peak Factor: </strong>
                <span>{{ billPeakFactor }}</span>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BModal, VBModal, BRow, BCol, BFormInput, BFormGroup, BForm, BButton,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import {
  reactive, ref, computed,
} from '@vue/composition-api'
import { debounce } from 'lodash'

export default {
  components: {
    BForm,
    BButton,
    BModal,
    BFormGroup,
    BRow,
    BCol,
    BFormInput,
    // flatPickr,
    vSelect,
  },
  directives: {
    'b-modal': VBModal,
  },
  props: {
    versionSettings: {
      type: Object,
      default: () => {},
    },
    currentZones: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, ctx) {
    const pickupPlace = ref(null)
    const dropoffPlace = ref(null)
    const pickupPlaces = ref([])
    const dropoffPlaces = ref([])
    const selectedPickupPlace = ref({})
    const selectedDropoffPlace = ref({})
    const selectedZone = ref(props.currentZones.length > 0 ? props.currentZones[0] : null)
    
    // Get zone objects from store based on zone IDs
    const zoneOptions = computed(() => {
      const allZones = ctx.root.$store.getters['zones/zones'] || []
      return allZones.filter(zone => props.currentZones.includes(zone.zone_id))
    })

    const fetchpickupPlaces = debounce(async (search, loading) => {
      if (!search) return
      loading(true)
      const res = await ctx.root.$store.dispatch('versions/searchPlaces', {
        text: search,
      })
      pickupPlaces.value = res.data.data.places
      loading(false)
    }, 1000)
    const fetchDropoffPlaces = debounce(async (search, loading) => {
      if (!search) return
      loading(true)
      const res = await ctx.root.$store.dispatch('versions/searchPlaces', {
        text: search,
      })
      dropoffPlaces.value = res.data.data.places
      loading(false)
    }, 1000)

    const onSelectedPlace = async type => {
      const res = await ctx.root.$store.dispatch('versions/getPlaceLatLong', {
        placeId: type === 'pickup' ? pickupPlace.value : dropoffPlace.value,
      })
      const { location } = res.data.data.results.at(0).geometry
      if (type === 'pickup') {
        selectedPickupPlace.value.lat = location.lat
        selectedPickupPlace.value.lng = location.lng
      } else {
        selectedDropoffPlace.value.lat = location.lat
        selectedDropoffPlace.value.lng = location.lng
      }
    }
    const totalTimeInMinutes = ref(null)
    const version = reactive({
      drop_zone: selectedZone.value,
      total_arrival_wait_time: 0,
      total_time: null,
      total_distance: null,
      create_time: new Date(),
    })
    
    const onZoneChange = (zoneId) => {
      version.drop_zone = zoneId
      selectedZone.value = zoneId
    }
    const matrixDistance = ref(null)
    const matrixTime = ref(null)
    const totalFare = ref(null)
    const estimateCalculated = ref(false)
    const calculatingEstimate = ref(false)
    const peakFactor = ref(null)
    const calcEstimate = async () => {
      calculatingEstimate.value = true
      estimateCalculated.value = false
      try {
        const matrixResult = await ctx.root.$store.dispatch('versions/distanceMatrix', {
          origins: `${selectedPickupPlace.value.lat},${selectedPickupPlace.value.lng}`,
          destinations: `${selectedDropoffPlace.value.lat},${selectedDropoffPlace.value.lng}`,
        })
        const temp = matrixResult.data.data.rows[0].elements[0]
        matrixDistance.value = temp.distance
        matrixTime.value = temp.duration
        const settingsTemp = { ...props.versionSettings }
        if (!settingsTemp.upfront_estimates.active) {
          delete settingsTemp.upfront_estimates
        }
        const statlessResult = await ctx.root.$store.dispatch('versions/testVersion', {
          settings: settingsTemp,
          inputs: {
            total_time: matrixTime.value.value,
            total_distance: matrixDistance.value.value / 1000,
            create_time: new Date(),
            drop_zone: selectedZone.value,
            total_arrival_wait_time: 0,
          },
        })
        totalFare.value = statlessResult.data.data.fare.total.toFixed(2)
        peakFactor.value = statlessResult.data.data.fare.details.peak_factors.factor
        estimateCalculated.value = true
      } finally {
        calculatingEstimate.value = false
      }
    }
    const isUpfrontEstimatedFieldsShown = computed(() => props.versionSettings.upfront_estimates.active)

    const billChosenTime = ref(null)
    const billChosenDistance = ref(null)
    const billTotal = ref(null)
    const calculatingBill = ref(false)
    const billCalculated = ref(false)
    const billPeakFactor = ref(null)
    const calcBill = async () => {
      try {
        calculatingBill.value = true
        billCalculated.value = false
        const temp = {
          ...version,
          total_arrival_wait_time: +version.total_arrival_wait_time * 60,
          total_time: version.total_time * 60,
          estimated_time: matrixTime.value.value,
          estimated_distance: matrixDistance.value.value,
          drop_zone: selectedZone.value,
        }
        // Convert to seconds
        const versionSettingsTemp = { ...props.versionSettings }
        versionSettingsTemp.upfront_estimates = { ...props.versionSettings.upfront_estimates }
        versionSettingsTemp.upfront_estimates.time = { ...props.versionSettings.upfront_estimates.time }
        if (props.versionSettings.upfront_estimates.active && props.versionSettings.upfront_estimates.time.rate === 'fixed') {
          versionSettingsTemp.upfront_estimates.time.maximum *= 60
          versionSettingsTemp.upfront_estimates.time.minimum *= 60
        }

        const response = await ctx.root.$store.dispatch('versions/testVersion', {
          settings: versionSettingsTemp,
          inputs: temp,
        })
        billTotal.value = response.data.data.fare.total.toFixed(2)
        billPeakFactor.value = response.data.data.fare.details.peak_factors.factor
        const fareDetails = response.data.data.fare.details
        const upfrontTemp = fareDetails.upfront_estimates?.details
        if ('distance' in upfrontTemp && 'time' in upfrontTemp) {
          billChosenTime.value = `${(upfrontTemp.time.value / 60).toFixed(2)} (${upfrontTemp.time.source}) Min`
          billChosenDistance.value = `${upfrontTemp.distance.value.toFixed(2)} (${upfrontTemp.distance.source}) KM`
        } else {
          billChosenTime.value = `${fareDetails.time_slicings.slices[fareDetails.time_slicings.slices.length - 1].to_unit} Min`
          billChosenDistance.value = `${fareDetails.distance_slicings.slices[fareDetails.distance_slicings.slices.length - 1].to_unit} KM`
        }
        calculatingBill.value = false
        billCalculated.value = true
      } catch (error) {
        console.log(error)
      }
    }

    const onDone = () => {
      version.total_arrival_wait_time = 0
      matrixDistance.value = null
      matrixTime.value = null
      totalFare.value = null
      estimateCalculated.value = false
      calculatingEstimate.value = false
      peakFactor.value = null
      billChosenTime.value = null
      billChosenDistance.value = null
      billTotal.value = null
      calculatingBill.value = false
      billCalculated.value = false
      billPeakFactor.value = null
      pickupPlace.value = null
      dropoffPlace.value = null
      version.total_distance = null
      version.total_time = null
    }
    return {
      onDone,
      calcBill,
      version,
      totalFare,
      totalTimeInMinutes,
      isUpfrontEstimatedFieldsShown,
      pickupPlace,
      dropoffPlace,
      pickupPlaces,
      dropoffPlaces,
      calculatingBill,
      selectedPickupPlace,
      selectedDropoffPlace,
      selectedZone,
      zoneOptions,
      onZoneChange,
      fetchpickupPlaces,
      fetchDropoffPlaces,
      onSelectedPlace,
      calcEstimate,
      billCalculated,
      estimateCalculated,
      matrixDistance,
      matrixTime,
      calculatingEstimate,
      billChosenTime,
      billChosenDistance,
      billTotal,
      billPeakFactor,
      peakFactor,
    }
  },
}
</script>

  <style scoped>
  </style>
